import { forwardRef, useEffect, useRef, useState } from "react";
import Files from "./FilemanagerFiles";
import LeftSideTree from "./FileManagerLeftSideTree";
import Toolbar from "./FileManagerToolbar";
import SplitLayout from "@CubeComponents/html/SplitLayout/SplitLayout";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "@Services/axiosWrapper";
import { CircularProgress } from "@mui/material";
import { showToastNotification } from "@Actions/toast";
import { TOAST_TYPE_WARNING } from "@Services/constants";
import { useDispatch } from "react-redux";
import { fileViewModes } from "./constants";

export interface ContextMenuItem {
  id: string;
  name: string;
  icon?: string;
  onClick?: (e: MouseEvent, payload?: unknown) => void;
}
interface FileManagerComponentProps {
  fileManagerData: any;
  allowDragAndDropUpload: boolean;
  onFileClick: Function;
  onFileSelect: Function;
  setCurrentFolder: Function;
  toolbarItems: any;
  toolbarClick: Function;
  contextMenuSettings?: {
    file: ContextMenuItem[];
    folder: ContextMenuItem[];
    layout: ContextMenuItem[];
    visible: boolean;
  };
  menuOpen: Function;
  fileColumns: any;
  setFileManagerData: Function;
  beforeSending: Function;
  onSuccess: Function;
  onFileLoad: Function;
  showFolderTree: boolean;
  path: string;
  allowViewFileOptions: boolean;
}
const FileManagerComponent = forwardRef(
  (
    {
      fileManagerData = {
        cwd: {},
        files: [],
      },
      allowDragAndDropUpload = false,
      onFileClick = () => {},
      onFileSelect = () => {},
      setCurrentFolder = () => {},
      toolbarItems = [],
      toolbarClick,
      contextMenuSettings = {
        file: [],
        folder: [],
        layout: [],
        visible: true,
      },
      menuOpen = () => {},
      fileColumns = [],
      setFileManagerData,
      beforeSending = () => {},
      onSuccess = () => {},
      onFileLoad = () => {},
      showFolderTree = true,
      path,
      allowViewFileOptions = false,
    }: FileManagerComponentProps,
    ref,
  ) => {
    const dispatch = useDispatch();
    const [selectedFolder, setSelectedFolder] = useState<any>({ id: "0" });
    const [filesViewMode, setFilesViewMode] = useState(fileViewModes.DETAILED_VIEW);
    const [tempFolder, setTempFolder] = useState({ id: "0" });
    const [refreshGrid, setRefreshGrid] = useState(false);
    const [selectedFilesInIconsMode, setSelectedFilesInIconsMode] = useState([]);
    const [isViewRendered, setIsViewRendered] = useState(false);
    const splitterSizeRef = useRef([30, 70]);
    const tempRef = useRef<any>({});
    const filesRef: any = ref || tempRef;
    const treeViewRef = useRef(null);

    const updateCurrentDir = (directory: any) => {
      setCurrentFolder(directory);
      setSelectedFolder(directory);
      setTempFolder(directory);
    };

    useEffect(() => {
      let size: any = [];
      if (!showFolderTree) {
        size = [0, 100];
      } else {
        size = [30, 70];
      }
      splitterSizeRef.current = size;
    }, [showFolderTree]);

    const fetchData = async (allIds = null, setDirectory = true) => {
      const allParams: any = {
        action: "read",
        path: "/",
      };
      if (allIds) {
        allParams.bulkParentIds = allIds;
        // allParams.path = `/${allIds.join("/")}/`
      }
      beforeSending(allParams);
      if (!allParams.cancel) {
        let allFiles = [];
        setRefreshGrid(true);
        const res = await axiosWrapper.post(`${getBackendUrl()}/fileSystemServer/`, {
          ...allParams,
        });
        if (res.data?.files) {
          const directory = { ...res.data.cwd, id: "0" };
          setFileManagerData(res.data);
          allFiles = res.data.files;
          if (setDirectory) {
            updateCurrentDir(directory);
          }
          onSuccess({
            result: res.data,
            action: "read",
            path: "/",
          });
        } else if (res.data.errorMessage) {
          if (res.data.cwd) {
            const directory = { ...res.data.cwd, id: "0", isRowDisabled: true };
            setFileManagerData({
              cwd: directory,
              files: [],
            });
          }
          dispatch(
            showToastNotification({
              content: res.data.errorMessage,
              type: TOAST_TYPE_WARNING,
            }),
          );
        }
        setRefreshGrid(false);
        return allFiles;
      }
    };

    const generatePath = (folder: any) => {
      const innerPath = folder.parentIdArray?.filter((id: string) => id != "0").join("/");
      const prefixPath = innerPath ? `/${innerPath}` : "";
      const suffixPath = folder.id == "0" ? "" : `${selectedFolder.id}/`;
      const finalPath = `${prefixPath}/${suffixPath}`;
      return finalPath;
    };

    const getMoreData = async () => {
      setRefreshGrid(true);
      const payload = { ...selectedFolder };
      const allParams: any = {
        action: "read",
        path: "/",
        data: [payload],
      };
      if (selectedFolder.id != "0") {
        const fetchPath = generatePath(selectedFolder);
        allParams.path = fetchPath;
      }
      beforeSending(allParams);
      if (!allParams.cancel) {
        const res = await axiosWrapper.post(`${getBackendUrl()}/fileSystemServer/`, {
          ...allParams,
        });
        if (res.data?.files) {
          const incomingFiles = res.data?.files || [];
          const incomingFileIds = incomingFiles?.map((file: any) => file.id) || [];
          let freshCurrentFolder = { ...selectedFolder };
          if (res.data?.cwd?._id && res.data?.cwd?._id != "0") {
            incomingFileIds.push(selectedFolder.id);
            freshCurrentFolder = res.data?.cwd;
            incomingFiles.push(freshCurrentFolder);
          }
          let currentFilteredFiles = fileManagerData.files?.filter((file: any) => !incomingFileIds.includes(file.id));
          currentFilteredFiles = currentFilteredFiles.filter((file: any) => file.parentId != selectedFolder.id);
          setFileManagerData((prev: any) => ({
            ...prev,
            files: [...incomingFiles, ...currentFilteredFiles],
          }));
          onSuccess({
            result: res.data,
            action: "read",
            path: allParams.path,
          });
          setCurrentFolder(freshCurrentFolder);
          setTempFolder(freshCurrentFolder);
        } else {
          dispatch(
            showToastNotification({
              content: res.data?.errorMessage || "",
              type: TOAST_TYPE_WARNING,
            }),
          );
        }
        setRefreshGrid(false);
      }
    };
    const fetchMoreData = async ({ selectedFolder }: { selectedFolder: any }) => {
      if (selectedFolder.id != "0") {
        await getMoreData();
      } else {
        setTempFolder(selectedFolder);
        setCurrentFolder(selectedFolder);
      }
    };

    const refreshFileManager = () => {
      fetchData();
      setSelectedFolder({ id: "0" });
    };

    const refreshFiles = () => {
      getMoreData();
    };

    function extractIds() {
      return path
        .split("/")
        .filter(Boolean)
        .filter((id) => id != "0");
    }

    const fetchBulkData = async () => {
      const allIds: any = extractIds();
      const allFiles = await fetchData(allIds, false);
      const lastFolderId = allIds[allIds.length - 1];
      const lastFolder = allFiles.find((file: any) => file.id == lastFolderId);
      updateCurrentDir({ ...lastFolder, isBulkFetch: true });
    };

    useEffect(() => {
      if (path) {
        const allIds = extractIds();
        if (allIds.length) {
          fetchBulkData();
        } else {
          fetchData();
        }
      }
    }, [path]);

    const getSelectedFilesInFileManager = () => {
      if (filesViewMode == fileViewModes.DETAILED_VIEW) {
        const selectedFile =
          filesRef.current.slickGrid
            .getSelectedRows()
            .map((data: any) => filesRef.current.slickGrid.getDataItem(data)) || [];
        return selectedFile;
      } else if (filesViewMode == fileViewModes.LARGE_ICONS_VIEW) {
        return selectedFilesInIconsMode;
      }
      return [];
    };

    useEffect(() => {
      if (filesRef.current) {
        filesRef.current.refreshFileManager = refreshFileManager;
        filesRef.current.refreshFiles = refreshFiles;
        filesRef.current.filesViewMode = filesViewMode;
        filesRef.current.getSelectedFilesInFileManager = getSelectedFilesInFileManager;
        filesRef.current.showLoading = () => {
          setRefreshGrid(true);
        };
        filesRef.current.hideLoading = () => {
          setRefreshGrid(false);
        };
      }
    }, [filesRef.current, isViewRendered, beforeSending, onSuccess, filesViewMode, selectedFilesInIconsMode]);

    useEffect(() => {
      if (!selectedFolder.isBulkFetch) {
        setSelectedFilesInIconsMode([]);
        fetchMoreData({ selectedFolder });
        setFileManagerData((prev: any) => ({
          ...prev,
          filteredFiles: null,
        }));
      }
    }, [selectedFolder?.id]);

    const handleSearchFiles = async (args: any) => {
      if (args) {
        setRefreshGrid(true);
        const payload = { ...selectedFolder };
        const allParams: any = {
          action: "search",
          caseSensitive: false,
          searchString: `*${args}*`,
          path: "/",
          data: [payload],
        };
        if (selectedFolder.id != "0") {
          const fetchPath = generatePath(selectedFolder);
          allParams.path = fetchPath;
        }
        beforeSending(allParams);
        if (!allParams.cancel) {
          delete allParams.cancel;
          const res = await axiosWrapper.post(`${getBackendUrl()}/fileSystemServer/`, {
            ...allParams,
          });
          if (res.data) {
            setFileManagerData((prev: any) => ({
              ...prev,
              filteredFiles: [...res.data.files],
            }));
          }
          onSuccess({
            result: res.data,
            action: "search",
            path: allParams.path,
          });
          setRefreshGrid(false);
        }
      } else {
        setFileManagerData((prev: any) => ({
          ...prev,
          filteredFiles: null,
        }));
      }
    };

    const getContent = () => {
      return (
        <div className="h-full flex flex-col w-full relative">
          {refreshGrid && (
            <div className="absolute h-full w-full flex justify-center items-center z-20 rounded-md">
              <CircularProgress />
            </div>
          )}
          <Toolbar
            setFilesViewMode={setFilesViewMode}
            filesViewMode={filesViewMode}
            allowViewFileOptions={allowViewFileOptions}
            toolbarClick={toolbarClick}
            toolbarItems={toolbarItems}
          />
          <SplitLayout
            initialSizes={splitterSizeRef.current}
            gutterWidth={"1px"}
            gutterStyle={{ position: "absolute", zIndex: 50 }}
            minSizes={[20, 45]}
            style={{ display: "flex", height: "100%" }}
            showProperties={true}
          >
            {[
              showFolderTree && (
                <div className="flex h-full border border-s-0 border-t-0 border-b-0 border-e-button_light overflow-auto">
                  <LeftSideTree
                    ref={treeViewRef}
                    onFileLoad={onFileLoad}
                    fileManagerData={fileManagerData}
                    selectedFolder={selectedFolder}
                    setSelectedFolder={setSelectedFolder}
                  />
                </div>
              ),
              <div key="filesGrid" className="h-full flex">
                <Files
                  ref={filesRef}
                  setIsViewRendered={setIsViewRendered}
                  isViewRendered={isViewRendered}
                  filesViewMode={filesViewMode}
                  setSelectedFilesInIconsMode={setSelectedFilesInIconsMode}
                  selectedFilesInIconsMode={selectedFilesInIconsMode}
                  allowDragAndDropUpload={allowDragAndDropUpload}
                  onFileLoad={onFileLoad}
                  tempFolder={tempFolder}
                  beforeSending={beforeSending}
                  handleSearchFiles={handleSearchFiles}
                  fileColumns={fileColumns}
                  menuOpen={menuOpen}
                  contextMenuSettings={contextMenuSettings}
                  fileManagerData={fileManagerData}
                  onFileSelect={onFileSelect}
                  onFileClick={onFileClick}
                  selectedFolder={selectedFolder}
                  setSelectedFolder={setSelectedFolder}
                />
              </div>,
            ].filter(Boolean)}
          </SplitLayout>
        </div>
      );
    };
    return <div className="w-full h-full overflow-auto flex flex-col">{getContent()}</div>;
  },
);
FileManagerComponent.displayName = "FileManagerComponent";
export default FileManagerComponent;
